/**
 * What to do when a trace source refuses its sign-in: an admin gets a
 * Reconnect button that opens the connection's edit form
 * (`/settings?tab=connections&edit=<id>`); anyone else is told who can.
 * qFlow holds no secret: the workbench signs in with its own AWS identity
 * through the connection's auth broker, so a refusal is fixed by checking the
 * broker URL and region on the connection, or else where the workbench runs.
 */

import { RefreshCw } from "lucide-react";
import { ButtonLink } from "@/components/ui/button";
import { useHolds } from "@/components/use-holds";
import { QFLOW, useSourceKind } from "@/features/traces/sources";

export const editConnectionPath = (id: string) => `/settings?tab=connections&edit=${encodeURIComponent(id)}`;

/** How a qFlow refusal is fixed: the connection's broker first, then the deployment. */
const QFLOW_FIX =
  "Check the connection's auth broker URL and region. If they are right, the workbench's AWS identity needs fixing: tell whoever deploys the workbench.";

/** `connectorType` when the caller knows it (a run's frozen connection); else it is looked up. */
export function Reconnect({ connectionId, connectorType }: { connectionId: string; connectorType?: string }) {
  const admin = useHolds("connections:write");
  const { type } = useSourceKind(connectionId);
  const qflow = (connectorType ?? type) === QFLOW;
  if (!admin) {
    const ask = qflow ? "Ask a workbench admin to check it." : "Ask a workbench admin to reconnect it.";
    return <span className="text-xs text-muted">{ask}</span>;
  }
  return (
    <ButtonLink size="sm" to={editConnectionPath(connectionId)}>
      <RefreshCw size={13} strokeWidth={2.2} aria-hidden />
      {qflow ? "Edit connection" : "Reconnect"}
    </ButtonLink>
  );
}

/** A source's sign-in failure, said plainly, with the way to fix it. */
export function SourceAuthError({ connectionId, label }: { connectionId: string; label: string }) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[9px] bg-warn-bg px-3 py-2"
    >
      <span className="flex-1 text-xs text-warn">
        {label === "qFlow"
          ? `qFlow refused the workbench's sign-in. ${QFLOW_FIX}`
          : `${label} refused the connection's sign-in: reconnect it.`}
      </span>
      <Reconnect connectionId={connectionId} />
    </div>
  );
}
